import { Box, Divider, Grid, useMediaQuery, useTheme } from '@mui/material';
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import RobotImage from "../../../public/main/Robot-3.png";
import RobotProvidersImage from "../../../public/main/Robot-21.png"; // New Image

export const ComparisonHome = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Box
            sx={{
                paddingY: '60px',
                paddingX: '20px',
                background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)',
                overflow: 'hidden',
                margin: { xs: 1, md: 8 },
                borderRadius: "20px"
            }}
        >
            {/* Section Title with Divider */}
            <Divider sx={{ borderColor: '#FF7600', marginBottom: '30px' }} role="presentation">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    <Typography
                        fontSize={{ xs: 26, md: 28 }}
                        fontWeight="700"
                        variant="h3"
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                            padding: '14px 24px',
                            color: '#32325d',
                            maxWidth: "90%",
                            margin: "0 auto",
                            transition: 'transform 0.3s ease',
                            '&:hover': { transform: 'scale(1.03)' },
                        }}
                    >
                        {t('main.howItWork.title')}
                    </Typography>
                </motion.div>
            </Divider>

            {/* First Content Section */}
            <Grid container spacing={4} alignItems="center">
                {/* Image on the Left */}
                <Grid item xs={12} md={6}>
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: "easeOut" }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: "100%",
                                maxWidth: { xs: "450px", md: "550px" }, // ✅ Responsive sizing
                                margin: "0 auto", // Centers image
                            }}
                        >
                            <motion.img
                                src={RobotImage}
                                alt="Rascoin Logo"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.3 }}
                                style={{
                                    width: "100%", // Takes full width of the Box
                                    height: "auto",
                                    display: "block",
                                    borderRadius: "15px",
                                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)"
                                }}
                            />
                        </Box>

                    </motion.div>
                </Grid>

                {/* Text on the Right */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ textAlign: 'center' }}>
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, ease: "easeOut" }}
                        >
                            <Typography
                                fontSize={{ xs: 22, md: 26 }}
                                fontWeight="700"
                                variant="h5"
                                sx={{
                                    color: '#32325d',
                                    marginBottom: '12px',
                                    transition: "color 0.3s ease",
                                    '&:hover': { color: '#FF7600' },
                                }}
                            >
                                {t('main.howItWork.robotBlock.preTitle')}
                            </Typography>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <Typography
                                color="#6B7280"
                                fontSize={{ xs: 16, md: 18 }}
                                variant="subtitle1"
                                component="p"
                                sx={{ lineHeight: 1.6, marginBottom: 2 }}
                            >
                                {t('main.howItWork.robotBlock.preDescription')}
                            </Typography>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, ease: "easeOut" }}
                        >
                            <Typography
                                fontSize={{ xs: 22, md: 24 }}
                                fontWeight="600"
                                variant="h6"
                                sx={{
                                    color: '#32325d',
                                    marginBottom: '12px',
                                    transition: "color 0.3s ease",
                                    '&:hover': { color: '#FF7600' },
                                }}
                            >
                                {t('main.howItWork.robotBlock.title')}
                            </Typography>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <Typography
                                color="#6B7280"
                                fontSize={{ xs: 16, md: 18 }}
                                variant="subtitle1"
                                component="p"
                                sx={{ lineHeight: 1.6 }}
                            >
                                {t('main.howItWork.robotBlock.description')}
                            </Typography>
                        </motion.div>
                    </Box>
                </Grid>
            </Grid>

            {/* NEW SECTION: ROBOT PROVIDERS */}
            <Divider sx={{ borderColor: 'grey', marginY: '40px' }} />

            <Grid container spacing={4} alignItems="center" sx={{ display: "flex", flexDirection: { xs: 'column-reverse', md: "row" }, }}>

                <Grid item xs={12} md={6}>
                    <Box sx={{ textAlign: 'center', maxWidth: '800px', marginY: '10px', marginX: { xs: '10px', md: "auto" } }}>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <Typography
                                fontSize={{ xs: 26, md: 28 }}
                                fontWeight="700"
                                variant="h4"
                                sx={{ marginBottom: '16px', color: '#32325d' }}
                            >
                                {t('main.howItWork.benefits.key')}
                            </Typography>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <Box
                                component="ul"
                                sx={{
                                    textAlign: 'left',
                                    display: 'inline-block',
                                    fontSize: { xs: 16, md: 18 },
                                    lineHeight: 1.6,
                                    color: '#6B7280',
                                    paddingLeft: { xs: "20px", md: "30px" },
                                }}
                            >
                                {[
                                    { title: t('main.howItWork.benefits.title1'), desc: t('main.howItWork.benefits.desc1') },
                                    { title: t('main.howItWork.benefits.title2'), desc: t('main.howItWork.benefits.desc2') },
                                    { title: t('main.howItWork.benefits.title3'), desc: t('main.howItWork.benefits.desc3') },
                                    { title: t('main.howItWork.benefits.title5'), desc: t('main.howItWork.benefits.desc5') },
                                ].map((item, index) => (
                                    <motion.li
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, ease: "easeOut", delay: index * 0.2 }}
                                        whileHover={{ scale: 1.05, color: "#FF7600" }}
                                        style={{ marginBottom: "10px", listStyle: "none" }}
                                    >
                                        <Typography fontWeight="600" component="span">
                                            {item.title}
                                        </Typography>{" "}
                                        – {item.desc}
                                    </motion.li>
                                ))}
                            </Box>
                        </motion.div>
                    </Box>
                </Grid>

                {/* Image on the Right */}
                <Grid item xs={12} md={6}>
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: "easeOut" }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <motion.img
                                src={RobotProvidersImage}
                                alt="Robot Providers"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.3 }}
                                style={{
                                    width: "100%",
                                    maxWidth: "550px", // ✅ Now a fixed value, but can be adjusted in Box
                                    height: "auto",
                                    display: "block",
                                    borderRadius: "15px",
                                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)"
                                }}
                            />
                        </Box>
                    </motion.div>
                </Grid>
                {/* Key Benefits Section */}
            </Grid>
            {/* <Divider sx={{ borderColor: 'grey', marginY: '40px' }} /> */}

            {/* <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', maxWidth: '800px', marginY: '10px', marginX: { xs: '10px', md: "auto" } }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        <Typography
                            fontSize={{ xs: 26, md: 28 }}
                            fontWeight="700"
                            variant="h4"
                            sx={{ marginBottom: '16px', color: '#32325d' }}
                        >
                            {t('main.howItWork.benefits.key')}
                        </Typography>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        <Box
                            component="ul"
                            sx={{
                                textAlign: 'left',
                                display: 'inline-block',
                                fontSize: { xs: 16, md: 18 },
                                lineHeight: 1.6,
                                color: '#6B7280'
                            }}
                        >
                            {[
                                { title: t('main.howItWork.benefits.title1'), desc: t('main.howItWork.benefits.desc1') },
                                { title: t('main.howItWork.benefits.title2'), desc: t('main.howItWork.benefits.desc2') },
                                { title: t('main.howItWork.benefits.title3'), desc: t('main.howItWork.benefits.desc3') },
                                { title: t('main.howItWork.benefits.title4'), desc: t('main.howItWork.benefits.desc4') },
                                { title: t('main.howItWork.benefits.title5'), desc: t('main.howItWork.benefits.desc5') },
                            ].map((item, index) => (
                                <motion.li
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, ease: "easeOut", delay: index * 0.2 }}
                                    whileHover={{ scale: 1.05, color: "#FF7600" }}
                                    style={{ marginBottom: "10px" }}
                                >
                                    <Typography fontWeight="600" component="span">
                                        {item.title}
                                    </Typography>{" "}
                                    – {item.desc}
                                </motion.li>
                            ))}
                        </Box>
                    </motion.div>
                </Box>
            </Grid> */}
        </Box>
    );
};
